import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  Mail01Icon, 
  AccessIcon,
  ViewIcon,
  ViewOffSlashIcon,
  ArrowRight01Icon
} from '@hugeicons/core-free-icons';
import { useAuth } from '../contexts/AuthContext';
import { FLAGS } from '../config/flags';
import { api } from '../utils/api';
import Button from '../components/Button';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();

  // If user is already logged in, redirect them away from login page
  useEffect(() => {
    if (user) {
      if (user.role === 'agent') {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, navigate]);

  const from = location.state?.from?.pathname || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    if (FLAGS.USE_MOCK_API) {
      await new Promise(resolve => setTimeout(resolve, 800));
      const mockUser = {
        id: 'usr_mock123',
        email: data.email,
        phone: '+234700000000',
        role: data.email.includes('agent') ? 'agent' : 'client',
        email_verified: true,
        deriv_linked: true
      };
      login(mockUser, 'mock-access-token-' + Math.random(), data.remember_me === 'on');
      
      if (from) {
        navigate(from, { replace: true });
      } else {
        navigate(mockUser.role === 'agent' ? '/dashboard' : '/');
      }
      setLoading(false);
      return;
    }

    try {
      const response = await api.post('/login', {
        login: data.email,
        password: data.password,
        remember_me: data.remember_me === 'on'
      });

      if (response.data && response.data.access_token) {
        const accessToken = response.data.access_token;
        const loginUser = response.data.user;
        let enrichedUser = { ...loginUser };

        // Temporarily set token for the subsequent request
        const storage = data.remember_me === 'on' ? localStorage : sessionStorage;
        storage.setItem('access_token', accessToken);

        try {
          // Fetch Deriv status immediately to populate the user object correctly
          const accountRes = await api.get('/deriv/my-accounts');
          const { user: fullProfile, has_deriv_account, deriv_account } = accountRes.data?.data || accountRes.data;
          
          // Flatten role if it's an object (Consistency Fix)
          let roleValue = fullProfile.role;
          if (typeof roleValue === 'object' && roleValue !== null) {
            roleValue = roleValue.name || roleValue.slug || 'client';
          }

          // Failsafe: check both flag and object existence
          const isLinked = !!has_deriv_account || !!deriv_account;

          enrichedUser = {
            ...loginUser,
            ...fullProfile,
            role: roleValue, // Save flattened string
            deriv_linked: isLinked
          };
        } catch (fetchErr) {
          console.warn('Failed to fetch initial deriv status, defaulting to unlinked:', fetchErr);
          // Fallback: If request fails, we assume unlinked, but user can still login
        }

        // Final login with enriched data
        // We MUST call login() to update AuthContext state so ProtectedRoute lets us through
        login(enrichedUser, accessToken, data.remember_me === 'on');
        
        // Redirect to Role Syncing gate instead of direct dashboard
        navigate('/auth-sync');
      }
    } catch (err) {
      if (err.status === 403 && err.data && !err.data.email_verified) {
        navigate('/email-verification?email=' + encodeURIComponent(data.email));
      } else {
        // Fix: Ensure message is always a string and has a clear fallback
        const msg = typeof err.message === 'string' ? err.message : 'Invalid email or password';
        setError({
          message: msg,
          retry_after: err.data?.retry_after,
          remaining_attempts: err.data?.remaining_attempts
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full space-y-8 bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md p-8 rounded-3xl border border-light-gray/20 dark:border-dark-border/20 shadow-sm">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-charcoal dark:text-white">Welcome Back</h2>
          <p className="mt-2 text-muted-gray">Log in to your Safzi account</p>
        </div>
        
        {error && (
          <div className="bg-red-100 dark:bg-red-900/20 border border-red-200/50 text-red-700 dark:text-red-300 px-4 py-3 rounded-2xl text-sm">
            <p className="font-bold">{error.message}</p>
            {error.retry_after && <p className="text-xs mt-1">Retry after: {Math.ceil(error.retry_after / 60)} mins</p>}
            {error.remaining_attempts !== undefined && <p className="text-xs mt-1">Remaining attempts: {error.remaining_attempts}</p>}
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">Email Address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                  <HugeiconsIcon icon={Mail01Icon} size={20} />
                </div>
                <input
                  type="email"
                  name="email"
                  required
                  className="block w-full pl-10 pr-3 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                  placeholder="name@company.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-charcoal dark:text-gray-200 mb-1">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-gray">
                  <HugeiconsIcon icon={AccessIcon} size={20} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  minLength={8}
                  className="block w-full pl-10 pr-10 py-3 border border-light-gray/20 dark:border-gray-700/50 rounded-2xl focus:ring-soft-green focus:border-soft-green text-charcoal dark:text-white bg-off-white/50 dark:bg-gray-800/50"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-gray hover:text-charcoal dark:hover:text-white"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <HugeiconsIcon icon={showPassword ? ViewOffSlashIcon : ViewIcon} size={20} />
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember_me"
                type="checkbox"
                defaultChecked={true}
                className="h-4 w-4 text-soft-green focus:ring-soft-green border-light-gray dark:border-gray-600 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-muted-gray">
                Remember me
              </label>
            </div>
            <div className="text-sm">
              <Link to="/forgot-password" className="font-medium text-soft-green hover:text-light-mint">
                Forgot password?
              </Link>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full relative"
          >
            {loading ? 'Signing In...' : 'Sign In'}
            <span className="absolute right-4 flex items-center">
              <HugeiconsIcon icon={ArrowRight01Icon} size={18} />
            </span>
          </Button>
        </form>

        <div className="text-center pt-4">
          <p className="text-sm text-muted-gray">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="font-bold text-soft-green hover:text-light-mint">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;